import { useButtonStyles_unstable } from "@fluentui/react-button";
import { shorthands, makeStyles, mergeClasses } from "@griffel/react";
import {
  getHighContrastNoAdjustStyle,
  HighContrastSelector,
} from "@fluentui/style-utilities";

import { customTokens } from "@1js/acui-theme";
import {
  menuTextChevronClass,
  menuTextContainerClass,
  menuTextLine2Class,
} from "@1js/acui-button";
import {
  ActionControlSize,
  baseFabricFocusAfterSelector,
  hiddenContentStyle,
  textContainerClassName,
  hiddenContentClassName,
} from "@1js/acui-common";
// eslint-disable-next-line no-restricted-imports
import {
  commonHighContrastBackgroundHoverFocus,
  commonHighContrastBorder,
  commonHighContrastButtonRestStyles,
  commonHighContrastColorButtonRest,
  commonHighContrastColorDisabled,
  commonHighContrastColorHoverFocus,
  commonHighContrastDisabledStyles,
  commonHighContrastHoveredDisabledStyles,
  commonHighContrastOutlineFocusedDisabled,
  commonHighContrastOutlineHoverFocus,
  TemporaryGriffelType,
} from "@1js/acui-common/lib/styling/next/commonStyles";

import { floatieStylingConstants } from "../../../ContextualUI/Controls/floatieStylingConstants";
import { getRibbonBottomBarStylingConstants } from "../../ribbonStylingConstants";
import { RibbonButtonState } from "../RibbonButton/next/RibbonButton.types";

// TODO 5340136: Replace with values from @1js/acui-button when babel-make-styles is fixed and we get to the variation of buttons in the peripheral controls area.
const borderRadius = 0;
const borderThicknessStandard: string = "1";
const borderThicknessFocused: string = "2";

/**
 * Holds the common ribbon button styles that gets applied to all types of ribbon buttons
 */
const useCommonRibbonButtonRootStyles = makeStyles({
  base: {
    position: "relative",
    boxShadow: "none",
    backgroundColor: "transparent",
    ...shorthands.borderColor("transparent"),
    ...shorthands.borderRadius(`${borderRadius}px`),
    focusInnerWidth: "0px",
    ...shorthands.borderWidth(`${borderThicknessStandard}px`),
    ...shorthands.borderStyle("solid"),
    ...shorthands.overflow("visible"),
    minWidth: "40px",
    paddingLeft: "9px",
    paddingRight: "9px",
    ...shorthands.gap("0px"),
    height: "40px",
    minHeight: "40px",
    fontWeight: 400,
    ":hover": {
      backgroundColor: customTokens.legacy_neutralLighter,
      ...shorthands.borderColor("transparent"),
      boxShadow: "none",
      cursor: "default",
    },

    ":active": {
      backgroundColor: customTokens.legacy_neutralQuaternaryAlt,
      ...shorthands.borderColor("transparent"),
      boxShadow: "none",
      color: customTokens.legacy_neutralDark,
      transform: "none",
    },

    [HighContrastSelector]: {
      ...(getHighContrastNoAdjustStyle() as TemporaryGriffelType),
      ...commonHighContrastButtonRestStyles,
      ...commonHighContrastColorButtonRest,
      ...commonHighContrastBorder,

      ":hover": {
        ...commonHighContrastBackgroundHoverFocus,
        ...commonHighContrastColorHoverFocus,
        ...commonHighContrastOutlineHoverFocus,
      },

      ":focus": {
        ...commonHighContrastBackgroundHoverFocus,
        ...commonHighContrastColorHoverFocus,
        ...commonHighContrastOutlineHoverFocus,
      },
    },

    lineHeight: "1",
    textOverflow: "unset",
    whiteSpace: "normal",
    [`& .${textContainerClassName}`]: {
      paddingLeft: "10px",
      paddingRight: "2px",
      whiteSpace: "nowrap",
    },
    [`& .${menuTextContainerClass}`]: {
      display: "flex",
      alignItems: "center",
      flexDirection: "column",
      padding: "1px",
      whiteSpace: "nowrap",

      [HighContrastSelector]: {
        ...getHighContrastNoAdjustStyle(),
      },
    },
    [`& .${menuTextLine2Class}`]: {
      lineHeight: "normal",
    },
    [`& .${menuTextChevronClass}`]: {
      width: "8px",
      height: "8px",
      padding: "0px 2px",
      fill: customTokens.legacy_neutralDark,
    },
    // Screen reader text
    [`& .${hiddenContentClassName}`]: {
      ...hiddenContentStyle,
    },
    [":focus::after"]: {
      content: '""',
      color: "initial",
      position: "absolute",
      left: "-1px",
      top: "-1px",
      bottom: "-1px",
      right: "-1px",
      ...shorthands.borderWidth(`${borderThicknessFocused}px`),
      ...shorthands.borderStyle("solid"),
      pointerEvents: "none",
    },
  },
  pixelFocusBorder: {
    ...shorthands.borderRadius(`${borderRadius}px`),
    focusInnerWidth: "0px",
  },
  disabled: {
    backgroundColor: "transparent",
    color: customTokens.legacy_neutralTertiaryAlt,
    ...shorthands.borderColor("transparent"),
    pointerEvents: "none",

    [HighContrastSelector]: {
      ...commonHighContrastColorDisabled,
      ...commonHighContrastDisabledStyles,
      ":hover": {
        ...commonHighContrastHoveredDisabledStyles,
      },
      ":focus": {
        ...commonHighContrastOutlineFocusedDisabled,
      },
    },
  },
  small: {
    fontSize: "12px",
    fontWeight: 400,
    ...shorthands.gap("0px"),
    height: "24px",
    minHeight: "24px",
    minWidth: "24px",
    ...shorthands.padding("1px"),
    justifyContent: "flex-start",
    [`& .${textContainerClassName}`]: {
      paddingLeft: "4px",
      paddingRight: "0px",
    },
  },
  large: {
    flexDirection: "column",
    fontSize: "12px",
    fontWeight: 400,
    ...shorthands.gap("0px"),
    height: "72px",
    justifyContent: "flex-start",
    lineHeight: "normal",
    [`& .${textContainerClassName}`]: {
      paddingLeft: "0px",
      paddingRight: "0px",
    },
  },
  // We need to offset the groupLauncher a bit to move it up to the edge of the divider.
  groupLauncher: {
    ...shorthands.gap("0px"),
    paddingLeft: "2px",
    paddingRight: "2px",
    width: "12px",
    minWidth: "12px",
    minHeight: "12px",
    height: "12px",
    marginRight: `${-getRibbonBottomBarStylingConstants("Multiline")
      .groupDividerPadding.left}px`,
    // the below styles are for the focus border
    justifyContent: "center",
    ":hover": {
      ...shorthands.borderRadius(`${borderRadius}px`),
    },
    ":active": {
      ...shorthands.borderRadius(`${borderRadius}px`),
    },
    [`& ${baseFabricFocusAfterSelector}`]: {
      top: "-3px",
      left: "-3px",
      right: "-3px",
    },
  },
  ribbonModeButton: {
    ...shorthands.gap("0px"),
    height: "16px",
    minHeight: "16px",
    width: "20px",
    minWidth: "20px",
    justifyContent: "flex-start",
    paddingLeft: "3px",
  },
  floatieButton: {
    ...shorthands.gap("0px"),
    fontSize: "12px",
    height: `${floatieStylingConstants.floatieRowHeight}px`,
    minHeight: `${floatieStylingConstants.floatieRowHeight}px`,
    minWidth: `${floatieStylingConstants.floatieRowHeight}px`,
    paddingLeft: "3px",
    paddingRight: "3px",
  },
  floatieLargeButton: {
    flexDirection: "column",
    fontSize: "12px",
    ...shorthands.gap("0px"),
    height: `${floatieStylingConstants.floatieLargeControlHeight}px`,
    justifyContent: "flex-start",
    lineHeight: "normal",
    minHeight: `${floatieStylingConstants.floatieLargeControlHeight}px`,
    minWidth: `${floatieStylingConstants.minimumFloatieLargeControlWidth}px`,
    paddingLeft: "2px",
    paddingRight: "2px",
    paddingTop: "4px",
    paddingBottom: "0px",
    ...shorthands.borderWidth(`${borderThicknessStandard}px`),
  },
});

const useCommonRibbonButtonIconStyles = makeStyles({
  base: {
    fontSize: "20px",
    height: "20px",
    width: "20px",
    "& i": {
      width: "inherit",
    },
  },
  small: {
    fontSize: "16px",
    height: "20x",
    width: "20x",
    "& i": {
      height: "16px",
      width: "16px",
      marginLeft: "2px",
    },
  },
  large: {
    fontSize: "32px",
    height: "32px",
    width: "32px",
    lineHeight: "32px",
  },
  groupLauncher: {
    fontSize: "8px",
    height: "8px",
    width: "8px",
  },
  ribbonModeButton: {
    fontSize: "12px",
    height: "12px",
    width: "12px",
  },
  ribbonModeButtonMultiline: {
    transform: "rotate(180deg)",
  },
  floatieLargeButton: {
    paddingLeft: "12px",
    paddingRight: "12px",
    fontSize: `${floatieStylingConstants.floatieLargeIconSize}px`,
    height: "100%",
    width: `${floatieStylingConstants.floatieLargeIconSize}px`,
  },
  disabled: {
    opacity: ".4",
    [HighContrastSelector]: {
      opacity: "1",
    },
  },
});

/**
 * checks whether the state/type of the ribbon button and applies the style based on that
 */
export const getRibbonButtonTypeStyles = (
  state: RibbonButtonState,
  styles: Record<string, string>
) => {
  if (state.variant === "ribbonModeButton") {
    return mergeClasses("ms-RibbonButton--ribbonMode", styles.ribbonModeButton);
  }
  if (state.variant === "groupLauncher") {
    return styles.groupLauncher;
  }
  return getRibbonButtonSizedStyles(state.size, styles);
};

/**
 * checks the size of the ribbon button and applies the style based on the size
 */
export const getRibbonButtonSizedStyles = (
  size: "small" | "medium" | "large",
  styles: Record<string, string>
) => {
  if (size === ActionControlSize.Small.toLowerCase()) {
    return styles.small;
  } else if (size === ActionControlSize.Large.toLowerCase()) {
    return styles.large;
  }
  return styles.medium;
};

/**
 * returns a state where all common ribbon button style are applied on the ribbon button
 */
export const useCommonRibbonButtonStyles = (
  state: RibbonButtonState
): RibbonButtonState => {
  const commonStyles = useCommonRibbonButtonRootStyles();
  const commonIconStyles = useCommonRibbonButtonIconStyles();
  state.root.className = mergeClasses(
    "ms-Button",
    commonStyles.base,
    commonStyles.pixelFocusBorder,
    (state.disabled || state.disabledFocusable) && commonStyles.disabled,
    getRibbonButtonTypeStyles(state, commonStyles),
    state.variant === "floatieButton" && commonStyles.floatieButton,
    state.variant === "floatieLargeButton" && commonStyles.floatieLargeButton,
    state.variant === "floatieButton" &&
      state.size === ActionControlSize.Large.toLowerCase() &&
      commonStyles.floatieLargeButton,
    state.root.className
  );

  if (state.icon) {
    state.icon.className = mergeClasses(
      "ms-RibbonButton-icon",
      commonIconStyles.base,
      (state.disabled || state.disabledFocusable) && commonIconStyles.disabled,
      getRibbonButtonTypeStyles(state, commonIconStyles),
      state.variantId === "ribbonModeButtonMultiline" &&
        commonIconStyles.ribbonModeButtonMultiline,
      state.variant === "floatieLargeButton" &&
        commonIconStyles.floatieLargeButton,
      state.variant === "floatieButton" &&
        state.size === ActionControlSize.Large.toLowerCase() &&
        commonIconStyles.floatieLargeButton,
      state.icon.className
    );
  }
  useButtonStyles_unstable(state);
  return state;
};
